import React, {Component} from 'react';
import styled, {css} from 'styled-components';

import {pxToRem} from 'utils/ui/units';
import {alpha} from 'utils/ui/colors';

import Icon from 'components/Icon';
import Text from 'components/Text';
import TextLink, {textLinkStyles} from 'components/TextLink';
import Time from 'components/Time';
import {typeScale, spacing, breakpoints} from 'styles/globals';
import {overflowWrap} from 'styles/mixins';
import {fadeIn} from 'styles/animations';

const senderColumnWidth = pxToRem(200);

const constantClassName = 'Message';

const borderRadius = p => pxToRem(p.theme.globals.borderRadius.small);

const Wrapper = styled.div.attrs({
	className: constantClassName,
})`
	position: relative;
	display: block;
	padding: ${spacing.s} ${spacing.m};

	background-color: ${p => p.theme.background};
	border: 1px solid ${p => alpha(p.theme.shade, p.theme.lineStrength)};

	&:first-child {
		border-top-left-radius: ${borderRadius};
		border-top-right-radius: ${borderRadius};
	}
	&:last-child {
		border-bottom-left-radius: ${borderRadius};
		border-bottom-right-radius: ${borderRadius};
	}

	${p =>
		p.isUnread &&
		css`
			box-shadow: inset ${spacing.xxs} 0 0 ${p.theme.highlight};
		`}

	.${constantClassName} + & {
		border-top: none;
	}
`;

const TimeWrapper = styled(Text)`
	float: right;
`;
TimeWrapper.defaultProps = {
	dimmed: true,
	size: 's',
};

const Sender = styled(Text)`
	padding-right: ${spacing.s};

	@media (min-width: ${breakpoints.m}) {
		position: absolute;
		top: ${pxToRem(15)};
		width: ${senderColumnWidth};
	}
`;
Sender.defaultProps = {
	as: 'div',
	dimmed: true,
	overflow: 'ellipsis',
};

const Body = styled.div`
	padding-top: ${spacing.xxs};

	@media (min-width: ${breakpoints.m}) {
		padding-left: ${senderColumnWidth};
	}
`;

const TitleTextLink = styled(({isUnread, ...otherProps}) => (
	<TextLink as="button" bold stealthy={!isUnread} {...otherProps} />
))`
	display: block;

	font-size: ${typeScale.l};
	line-height: 1;
	text-align: left;

	${overflowWrap}
`;

const Chevron = styled(Icon).withConfig({
	shouldForwardProp: prop => prop !== 'isOpen',
})`
	transform: rotate(-180deg);
	transition: all 0.25s ease-in;

	${p =>
		!p.isOpen &&
		css`
			transform: none;
			transition-timing-function: ease-out;

			@media (min-width: ${breakpoints.m}) {
				opacity: 0;
			}

			${TitleTextLink}:hover &,
		${TitleTextLink}:focus & {
				opacity: 1;
			}
		`}
`;
Chevron.defaultProps = {
	name: 'chevron',
	size: 'unscaled',
};

const MessageContent = styled.div`
	margin-top: ${spacing.s};
	animation: 250ms ${fadeIn} linear;

	${p =>
		!p.isOpen &&
		css`
			display: none;
		`}

	& a {
		font-weight: bold;

		${textLinkStyles}
	}
`;

class Message extends Component {
	constructor(props) {
		super(props);

		this.state = {
			isOpen: false,
		};

		this.toggleOpen = this.toggleOpen.bind(this);
	}

	toggleOpen() {
		if (this.state.isOpen) {
			this.setState({isOpen: false});
		} else {
			this.setState({isOpen: true});

			if (this.props.onOpen) {
				this.props.onOpen(this.props.id);
			}
		}
	}

	render() {
		const {isOpen} = this.state;

		const {isUnread, id, time, sender, subject, children, ...otherProps} =
			this.props;

		delete otherProps.id;
		delete otherProps.onOpen;

		const messageContentId = `message-${id}`;

		return (
			<Wrapper {...otherProps} isUnread={isUnread}>
				<TimeWrapper>
					<Time dateTime={time} />
				</TimeWrapper>
				<Sender>{sender}</Sender>
				<Body>
					<TitleTextLink
						onClick={this.toggleOpen}
						isUnread={isUnread}
						aria-expanded={isOpen ? 'true' : 'false'}
						aria-controls={messageContentId}
					>
						{subject}
						<Chevron isOpen={isOpen} />
					</TitleTextLink>
					<MessageContent isOpen={isOpen} id={messageContentId}>
						{children}
					</MessageContent>
				</Body>
			</Wrapper>
		);
	}
}

export default Message;
